import React from "react";
import DOMPurify from "dompurify";
import { dialog } from "../../components/ui/Dialog";
import { Icon, PrIco } from "./ProcUi";
import { redigerIaApi, supprimerFichiersIaApi, televerserFichierIa } from "./procApi";
import { IA_ACCEPT, IA_FICHIERS_MAX, IA_GENRES, IA_TEXTE_MAX, libererApercu, lireFichier, tailleLisible } from "./procIaFichiers";

// Rédaction d'une procédure par l'IA (pas de maquette : écran composé avec les
// éléments du manuel — fenêtre, champs, sélecteur, aperçu de page).
// À gauche la demande : consigne, format, documents joints. À droite l'aperçu :
// le brouillon, qui s'écrit en direct, et chaque document joint. Rien n'est
// enregistré tant que l'auteur ne crée pas la page ; elle s'ouvre alors en
// brouillon dans l'éditeur.

const FORMATS = [
  ["proc", "Pas à pas"],
  ["check", "Liste de contrôle"],
  ["faq", "Questions fréquentes"],
];
const ETAPES = {
  envoi: "Envoi des documents…",
  lecture: "Lecture des documents et du manuel…",
  redaction: "Rédaction en cours…",
};
const sain = (html) => DOMPurify.sanitize(html || "", { ADD_ATTR: ["target"] });
const nbAVerifier = (html) => (String(html || "").match(/À vérifier/g) || []).length;
const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? "s" : ""}`;

// Aperçu d'un document joint, selon son genre.
function ApercuFichier({ f }) {
  const [feuille, setFeuille] = React.useState(0);
  if (f.genre === "pdf") return <iframe className="pr-ia-frame" src={f.apercu.url} title={f.nom}></iframe>;
  if (f.genre === "image") return <div className="pr-ia-imgbox"><img className="pr-ia-img" src={f.apercu.url} alt={f.nom} /></div>;
  if (f.genre === "word") {
    return (
      <div className="pr-ia-page">
        <div className="pr-content pr-ia-word" dangerouslySetInnerHTML={{ __html: f.apercu.html }}></div>
      </div>
    );
  }
  if (f.genre === "excel") {
    const fs = f.apercu.feuilles;
    const i = Math.min(feuille, fs.length - 1);
    return (
      <div className="pr-ia-excel">
        {fs.length > 1 && (
          <div className="pr-seg">
            {fs.map((x, k) => <button key={x.nom} className={k === i ? "on" : ""} onClick={() => setFeuille(k)}>{x.nom}</button>)}
          </div>
        )}
        <div className="pr-ia-sheet" dangerouslySetInnerHTML={{ __html: fs[i].html }}></div>
      </div>
    );
  }
  return <pre className="pr-ia-pre">{f.apercu.texte}</pre>;
}

export default function ProcRedactionIA({ init, emplacement, onClose, onCreer }) {
  const [consigne, setConsigne] = React.useState("");
  const [format, setFormat] = React.useState("proc");
  const [fichiers, setFichiers] = React.useState([]);
  const [vue, setVue] = React.useState("brouillon");
  const [over, setOver] = React.useState(false);
  const [refus, setRefus] = React.useState([]);
  const [r, setR] = React.useState({ etat: "vide", etape: "", html: "", message: "", avertissement: null });
  const [secondes, setSecondes] = React.useState(0);
  const [creation, setCreation] = React.useState(false);
  const inputRef = React.useRef(null);
  const ctrl = React.useRef(null);
  const brut = React.useRef("");
  const minuteur = React.useRef(null);
  const panneau = React.useRef(null);
  const fichiersRef = React.useRef(fichiers);
  fichiersRef.current = fichiers;

  // Fermeture : rédaction arrêtée, aperçus libérés, fichiers envoyés supprimés.
  React.useEffect(
    () => () => {
      if (ctrl.current) ctrl.current.abort();
      clearInterval(minuteur.current);
      fichiersRef.current.forEach(libererApercu);
      const urls = fichiersRef.current.map((f) => f.url).filter(Boolean);
      if (urls.length) supprimerFichiersIaApi(urls).catch(() => {});
    },
    [],
  );

  const enCours = r.etat === "envoi" || r.etat === "redaction";

  // Le brouillon défile pendant qu'il s'écrit.
  React.useEffect(() => {
    if (enCours && vue === "brouillon" && panneau.current) panneau.current.scrollTop = panneau.current.scrollHeight;
  }, [r.html, enCours, vue]);

  const ajouter = async (liste) => {
    const place = IA_FICHIERS_MAX - fichiersRef.current.length;
    const ko = liste.length > place ? [`${IA_FICHIERS_MAX} fichiers au maximum`] : [];
    const retenus = [...liste].slice(0, Math.max(0, place)).map((file) => ({
      cle: Math.random().toString(36).slice(2),
      nom: file.name,
      taille: file.size,
      etat: "lecture",
      file,
    }));
    setRefus([]);
    setFichiers((fs) => [...fs, ...retenus]);
    let premier = true;
    for (const f of retenus) {
      try {
        const lu = await lireFichier(f.file);
        setFichiers((fs) => fs.map((x) => (x.cle === f.cle ? { ...x, ...lu, etat: "pret" } : x)));
        // Tant qu'il n'y a pas de brouillon, on montre le document ajouté.
        if (premier && !brut.current) setVue(f.cle);
        premier = false;
      } catch (e) {
        ko.push(`${f.nom} (${e.message})`);
        setFichiers((fs) => fs.filter((x) => x.cle !== f.cle));
      }
    }
    setRefus(ko);
  };

  const retirer = (f) => {
    libererApercu(f);
    if (f.url) supprimerFichiersIaApi([f.url]).catch(() => {});
    setFichiers((fs) => fs.filter((x) => x.cle !== f.cle));
    if (vue === f.cle) setVue("brouillon");
  };

  const caracteres = fichiers.reduce((s, f) => s + (f.caracteres || 0), 0);
  const tropGros = caracteres > IA_TEXTE_MAX;
  const pret = !enCours && !creation && (consigne.trim() || fichiers.length > 0) && fichiers.every((f) => f.etat === "pret") && !tropGros;

  const rediger = async () => {
    if (!pret) return;
    const c = new AbortController();
    ctrl.current = c;
    brut.current = "";
    setVue("brouillon");
    setR({ etat: "envoi", etape: fichiers.length ? "envoi" : "redaction", html: "", message: "", avertissement: null });
    const debut = Date.now();
    setSecondes(0);
    clearInterval(minuteur.current);
    minuteur.current = setInterval(() => setSecondes(Math.round((Date.now() - debut) / 1000)), 1000);
    try {
      // 1. Documents vers Vercel Blob (une seule fois par document).
      const envoyes = [];
      for (const f of fichiersRef.current) {
        let url = f.url;
        if (!url) {
          url = (await televerserFichierIa(f.envoi.fichier, f.envoi.fichier.name)).url;
          setFichiers((fs) => fs.map((x) => (x.cle === f.cle ? { ...x, url } : x)));
        }
        envoyes.push({ nom: f.nom, type: f.envoi.type, url });
      }
      if (c.signal.aborted) throw new DOMException("Arrêt", "AbortError");
      setR((x) => ({ ...x, etat: "redaction", etape: envoyes.length ? "lecture" : "redaction" }));

      // 2. Rédaction, affichée par lots pendant qu'elle s'écrit.
      let affiche = 0;
      await redigerIaApi(
        { titre: init.title, consigne, format, espaceId: init.spaceId, sectionId: init.sectionId, fichiers: envoyes },
        (e) => {
          if (e.t === "etape") setR((x) => ({ ...x, etape: e.e }));
          else if (e.t === "texte") {
            brut.current += e.d;
            if (Date.now() - affiche > 150) {
              affiche = Date.now();
              setR((x) => ({ ...x, etape: "redaction", html: brut.current }));
            }
          } else if (e.t === "reprise") {
            brut.current = "";
            setR((x) => ({ ...x, html: "" }));
          } else if (e.t === "fin") {
            brut.current = e.html;
            setR({ etat: "fini", etape: "", html: e.html, message: "", avertissement: e.avertissement });
          } else if (e.t === "erreur") {
            setR((x) => ({ ...x, etat: "erreur", etape: "", message: e.message }));
          }
        },
        c.signal,
      );
      setR((x) =>
        x.etat === "redaction"
          ? { ...x, etat: "erreur", message: "La connexion a été coupée avant la fin du brouillon. Relancez la rédaction." }
          : x,
      );
    } catch (e) {
      if (c.signal.aborted) setR((x) => ({ ...x, etat: "arrete", etape: "", html: brut.current }));
      else {
        console.error("Procédures — rédaction par l'IA :", e);
        setR((x) => ({ ...x, etat: "erreur", etape: "", message: e.message || "La rédaction a échoué." }));
      }
    } finally {
      clearInterval(minuteur.current);
      if (ctrl.current === c) ctrl.current = null;
    }
  };

  const fermer = async () => {
    if (enCours || r.etat === "fini") {
      const ok = await dialog.confirm(
        enCours ? "La rédaction est en cours. L'arrêter et fermer ?" : "Le brouillon n'a pas encore été enregistré dans le manuel. Fermer quand même ?",
        { variant: "warning", title: "Fermer la rédaction", confirmLabel: "Fermer", cancelLabel: "Rester" },
      );
      if (!ok) return;
    }
    onClose();
  };

  const creer = async () => {
    setCreation(true);
    if (!(await onCreer(r.html))) setCreation(false);
  };

  // Les liens internes de l'aperçu ne quittent pas la fenêtre.
  const bloquerLiens = (e) => {
    if (e.target.closest("a")) e.preventDefault();
  };

  const aVerifier = nbAVerifier(r.html);
  const statut = enCours ? (
    <><span className="pr-ia-dot"></span>{ETAPES[r.etape] || ETAPES.redaction} {secondes} s</>
  ) : r.etat === "fini" ? (
    aVerifier ? <><Icon name="alert" size={14} /> {pluriel(aVerifier, "point")} à vérifier dans le brouillon</> : <><Icon name="check" size={14} /> Brouillon prêt</>
  ) : r.etat === "arrete" ? (
    "Rédaction arrêtée."
  ) : r.etat === "erreur" ? (
    <span className="pr-ia-err">{r.message}</span>
  ) : null;

  const fichierVu = fichiers.find((f) => f.cle === vue && f.etat === "pret");

  return (
    <div className="scrim">
      <div className="modal pr-ia-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="icon-square"><PrIco n="spark" s={22} /></div>
          <div>
            <h2>Rédiger avec l'IA</h2>
            <div className="meta">
              <span className="pr-ia-meta"><b>{init.title}</b>{emplacement ? ` · ${emplacement}` : ""}</span>
            </div>
          </div>
          <div className="actions">
            <button className="x-btn" onClick={fermer} title="Fermer"><Icon name="x" size={16} /></button>
          </div>
        </div>

        <div className="pr-ia-body">
          {/* Demande */}
          <div className="pr-ia-form">
            <label className="pr-f-lab" htmlFor="pr-ia-consigne">Votre demande</label>
            <textarea
              id="pr-ia-consigne"
              className="pr-ia-ta"
              autoFocus
              value={consigne}
              disabled={enCours}
              onChange={(e) => setConsigne(e.target.value)}
              placeholder="Décrivez la procédure : pour qui, les étapes que vous connaissez, les outils, les délais, les points d'attention… Vous pouvez aussi simplement joindre une ancienne procédure ou une fiche."
            />

            <label className="pr-f-lab">Format</label>
            <div className="pr-seg">
              {FORMATS.map(([v, l]) => (
                <button key={v} className={format === v ? "on" : ""} disabled={enCours} onClick={() => setFormat(v)}>{l}</button>
              ))}
            </div>

            <label className="pr-f-lab">Documents joints <span className="pr-ia-opt">facultatif</span></label>
            <div
              className={`pr-ia-drop ${over ? "over" : ""} ${enCours ? "off" : ""}`}
              onClick={() => !enCours && inputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); if (!enCours) setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); if (!enCours) ajouter(e.dataTransfer.files); }}
            >
              <input ref={inputRef} type="file" multiple hidden accept={IA_ACCEPT} onChange={(e) => { ajouter(e.target.files); e.target.value = ""; }} />
              <PrIco n="upload" s={18} />
              <div>
                <b>Glisser des fichiers ici ou cliquer pour parcourir</b>
                <small>PDF, Word, Excel, images ou texte · 20 Mo maximum par fichier</small>
              </div>
            </div>
            {refus.length > 0 && <div className="pr-ia-refus"><Icon name="alert" size={13} /> Non joint : {refus.join(", ")}.</div>}
            {fichiers.length > 0 && (
              <div className="pr-ia-files">
                {fichiers.map((f) => (
                  <div
                    key={f.cle}
                    role="button"
                    tabIndex={0}
                    className={`pr-ia-file ${vue === f.cle ? "on" : ""}`}
                    onClick={() => f.etat === "pret" && setVue(f.cle)}
                    onKeyDown={(e) => e.key === "Enter" && f.etat === "pret" && setVue(f.cle)}
                  >
                    <span className={`pr-ia-file-ico g-${f.genre || "lecture"}`}>{f.etat === "lecture" ? "…" : IA_GENRES[f.genre]}</span>
                    <span className="pr-ia-file-m">
                      <span className="pr-ia-file-n" title={f.nom}>{f.nom}</span>
                      <span className="pr-ia-file-s">{f.etat === "lecture" ? "Lecture du fichier…" : `${tailleLisible(f.taille)} · ${vue === f.cle ? "affiché à droite" : "cliquer pour l'aperçu"}`}</span>
                    </span>
                    <button className="x-btn pr-ia-x" title="Retirer" disabled={enCours} onClick={(e) => { e.stopPropagation(); retirer(f); }}>
                      <Icon name="x" size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {tropGros && <div className="pr-ia-refus"><Icon name="alert" size={13} /> Documents trop volumineux pour une seule rédaction : retirez un fichier ou gardez seulement les parties utiles.</div>}

            <div className="pr-ia-note">
              <Icon name="info" size={14} />
              <span>
                L'IA s'appuie sur votre demande, vos documents et le manuel existant, avec des liens vers les procédures liées. Ce qu'elle ne peut pas confirmer est signalé « À vérifier ». Rien n'est publié : vous relisez le brouillon ici, puis dans l'éditeur.
              </span>
            </div>
          </div>

          {/* Aperçu */}
          <div className="pr-ia-view">
            <div className="pr-ia-tabs">
              <button className={`pr-ia-tab ${vue === "brouillon" ? "on" : ""}`} onClick={() => setVue("brouillon")}>
                <PrIco n="spark" s={13} /> Brouillon
              </button>
              {fichiers.filter((f) => f.etat === "pret").map((f) => (
                <button key={f.cle} className={`pr-ia-tab ${vue === f.cle ? "on" : ""}`} title={f.nom} onClick={() => setVue(f.cle)}>
                  <PrIco n="doc" s={13} /> <span>{f.nom}</span>
                </button>
              ))}
            </div>
            <div className={`pr-ia-pane ${fichierVu && fichierVu.genre === "pdf" ? "plein" : ""}`} ref={panneau}>
              {fichierVu ? (
                <ApercuFichier key={fichierVu.cle} f={fichierVu} />
              ) : r.html ? (
                <div className="pr-ia-page" onClick={bloquerLiens}>
                  {r.avertissement && <div className="pr-ia-alerte"><Icon name="alert" size={14} /> {r.avertissement}</div>}
                  <h1 className="pr-ia-titre">{init.title}</h1>
                  <div className="pr-content" dangerouslySetInnerHTML={{ __html: sain(r.html) }}></div>
                  {enCours && <div className="pr-ia-curseur"><span className="pr-ia-dot"></span> Rédaction en cours…</div>}
                </div>
              ) : enCours ? (
                <div className="pr-ia-empty">
                  <div>
                    <div className="pr-ia-empty-ico pulse"><PrIco n="spark" s={26} /></div>
                    <b>{ETAPES[r.etape] || ETAPES.redaction}</b>
                    <p>Le brouillon s'affichera ici au fur et à mesure de sa rédaction.</p>
                  </div>
                </div>
              ) : r.etat === "erreur" ? (
                <div className="pr-ia-empty">
                  <div>
                    <div className="pr-ia-empty-ico err"><Icon name="alert" size={24} /></div>
                    <b>La rédaction n'a pas abouti</b>
                    <p>{r.message}</p>
                  </div>
                </div>
              ) : (
                <div className="pr-ia-empty">
                  <div>
                    <div className="pr-ia-empty-ico"><PrIco n="spark" s={26} /></div>
                    <b>Le brouillon apparaîtra ici</b>
                    <ol className="pr-ia-steps">
                      <li>Décrivez la procédure, joignez des documents si vous en avez.</li>
                      <li>Cliquez sur « Rédiger le brouillon » et suivez la rédaction en direct.</li>
                      <li>Relisez, régénérez si besoin, puis créez la page pour l'ajuster dans l'éditeur.</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pr-ia-foot">
          <div className="pr-ia-status">{statut}</div>
          <button className="btn" onClick={fermer}>Annuler</button>
          {enCours ? (
            <button className="btn" onClick={() => ctrl.current && ctrl.current.abort()}>Arrêter</button>
          ) : (
            <button className={`btn ${r.etat === "fini" ? "" : "primary"}`} disabled={!pret} onClick={rediger}>
              <PrIco n="spark" s={14} /> {r.etat === "vide" ? "Rédiger le brouillon" : "Régénérer"}
            </button>
          )}
          {r.etat === "fini" && (
            <button className="btn primary" disabled={creation} onClick={creer}>
              <Icon name="check" size={14} /> {creation ? "Création…" : "Créer la page et l'ouvrir dans l'éditeur"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
